import { Check } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';

export type MenuEntry =
  | { kind: 'link'; id: string; label: ReactNode; to: string; current?: boolean; icon?: ReactNode }
  | {
      kind: 'action';
      id: string;
      label: ReactNode;
      onSelect: () => void;
      icon?: ReactNode;
      hint?: ReactNode;
      /** Shown but not actionable; the reason is read with the item. */
      disabledReason?: string;
    }
  | { kind: 'radio'; id: string; label: ReactNode; checked: boolean; onSelect: () => void; icon?: ReactNode }
  /** A toggle that keeps the menu open (`menuitemcheckbox`). */
  | { kind: 'check'; id: string; label: ReactNode; checked: boolean; onSelect: () => void }
  /** Not actionable: loading, empty or error lines. */
  | { kind: 'note'; id: string; label: ReactNode }
  | { kind: 'separator'; id: string };

export type MenuPlacement = 'below-start' | 'below-end' | 'above-end' | 'right-end';

const PLACEMENT: Record<MenuPlacement, string> = {
  'below-start': 'left-0 top-full mt-1',
  'below-end': 'right-0 top-full mt-1',
  'above-end': 'right-0 bottom-full mb-1',
  'right-end': 'bottom-0 left-full ml-2',
};

const ITEM_SELECTOR = '[role^="menuitem"]:not([aria-disabled="true"])';

/**
 * A menu button with a popover menu (spec 6.1.7): Enter, Space or ArrowDown open it and focus the current
 * or first item, ArrowUp/ArrowDown/Home/End move, Enter or Space activates, Esc closes and returns focus to
 * the button, Tab or a click outside closes it.
 */
export function MenuButton({
  buttonContent,
  buttonLabel,
  buttonTitle,
  buttonClassName,
  menuLabel,
  entries,
  header,
  footer,
  placement = 'below-start',
  onOpenChange,
  wrapperClassName,
  buttonCurrent,
  portal,
  buttonProps,
}: {
  buttonContent: ReactNode;
  buttonLabel?: string;
  buttonTitle?: string;
  buttonClassName: string;
  menuLabel: string;
  entries: MenuEntry[];
  header?: ReactNode;
  footer?: ReactNode;
  placement?: MenuPlacement;
  onOpenChange?: (open: boolean) => void;
  wrapperClassName?: string;
  /** Marks the button as the current destination (`aria-current="page"`). */
  buttonCurrent?: boolean;
  /**
   * Renders the menu in a portal at a fixed position next to the button, so containers that clip their
   * content (tool windows, scrolling tables) don't cut it off. It opens above the button when there is no
   * room below.
   */
  portal?: boolean;
  /** Extra attributes for the button (data attributes, tabIndex inside grids). */
  buttonProps?: Record<string, string | number | undefined>;
}) {
  const [open, setOpenState] = useState(false);
  const [focusLast, setFocusLast] = useState(false);
  const menuId = useId();
  const describedId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [fixed, setFixed] = useState<CSSProperties | null>(null);

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);

  // Focus the current (or checked) item, else the first one; the menu itself while nothing is actionable.
  const itemKey = entries.map((e) => `${e.kind}:${e.id}`).join('|');
  useEffect(() => {
    if (!open) return;
    const all = items();
    const current = all.find(
      (item) => item.getAttribute('aria-current') === 'true' || item.getAttribute('aria-checked') === 'true',
    );
    const target = focusLast ? all[all.length - 1] : (current ?? all[0]);
    if (target) target.focus();
    else menuRef.current?.focus();
    // Re-run when the entries arrive (lists load when the menu opens), unless focus is already on an item.
  }, [open, focusLast, itemKey]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, setOpen]);

  // A portalled menu sits under (or above) the button, aligned per placement, inside the viewport.
  useLayoutEffect(() => {
    if (!open || !portal) return;
    const rect = buttonRef.current?.getBoundingClientRect();
    const menu = popoverRef.current;
    if (!rect || !menu) return;
    const width = menu.offsetWidth;
    const height = menu.offsetHeight;
    const alignEnd = placement.endsWith('end');
    let left = alignEnd ? rect.right - width : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const below = rect.bottom + 4;
    const top = below + height > window.innerHeight - 8 ? Math.max(8, rect.top - height - 4) : below;
    setFixed({ position: 'fixed', top, left, right: 'auto', bottom: 'auto', marginTop: 0 });
  }, [open, portal, placement, itemKey]);

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusLast(event.key === 'ArrowUp');
      setOpen(true);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const all = items();
    const index = all.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => {
      event.preventDefault();
      if (all.length > 0) all[(to + all.length) % all.length].focus();
    };
    switch (event.key) {
      case 'ArrowDown':
        move(index + 1);
        break;
      case 'ArrowUp':
        move(index < 0 ? all.length - 1 : index - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(all.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case 'Tab':
        setOpen(false);
        break;
      case ' ':
        if (index >= 0) {
          event.preventDefault();
          all[index].click();
        }
        break;
    }
  };

  const renderPopover = (node: ReactNode) => (portal ? createPortal(node, document.body) : node);

  const activate = (entry: Extract<MenuEntry, { onSelect: () => void }>) => {
    // Focus the button first, so a dialog the action opens returns focus there when it closes.
    close(true);
    entry.onSelect();
  };

  return (
    <div ref={wrapperRef} className={`relative ${wrapperClassName ?? 'inline-flex'}`}>
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        aria-label={buttonLabel}
        title={buttonTitle}
        aria-haspopup="menu"
        aria-current={buttonCurrent ? 'page' : undefined}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        {...buttonProps}
        onClick={() => {
          setFocusLast(false);
          setOpen(!open);
        }}
        onKeyDown={onButtonKeyDown}
      >
        {buttonContent}
      </button>
      {open &&
        renderPopover(
          <div
            ref={popoverRef}
            className={`popover ${portal ? '' : PLACEMENT[placement]}`}
            style={portal ? (fixed ?? { position: 'fixed', top: -9999, left: -9999 }) : undefined}
          >
            {header && <div id={describedId}>{header}</div>}
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-label={menuLabel}
              aria-describedby={header ? describedId : undefined}
              tabIndex={-1}
              onKeyDown={onMenuKeyDown}
              className="max-h-[60vh] overflow-y-auto outline-none"
            >
              {entries.map((entry) => {
                switch (entry.kind) {
                  case 'separator':
                    return <div key={entry.id} role="separator" className="menu-separator" />;
                  case 'note':
                    return (
                      <div key={entry.id} role="menuitem" aria-disabled="true" className="menu-item">
                        {entry.label}
                      </div>
                    );
                  case 'link':
                    return (
                      <Link
                        key={entry.id}
                        role="menuitem"
                        tabIndex={-1}
                        to={entry.to}
                        aria-current={entry.current ? 'true' : undefined}
                        className="menu-item"
                        onClick={() => close(true)}
                      >
                        {entry.icon}
                        <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                        {entry.current && <Check aria-hidden className="size-3.5" />}
                      </Link>
                    );
                  case 'action':
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        role="menuitem"
                        tabIndex={-1}
                        className="menu-item"
                        aria-disabled={entry.disabledReason ? true : undefined}
                        title={entry.disabledReason}
                        onClick={() => !entry.disabledReason && activate(entry)}
                      >
                        {entry.icon}
                        <span className="min-w-0 flex-1 truncate">
                          {entry.label}
                          {entry.disabledReason && <span className="sr-only">, {entry.disabledReason}</span>}
                        </span>
                        {entry.hint}
                      </button>
                    );
                  case 'check':
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={entry.checked}
                        tabIndex={-1}
                        className="menu-item"
                        onClick={entry.onSelect}
                      >
                        <span className="inline-flex size-3.5 items-center">
                          {entry.checked && <Check aria-hidden className="size-3.5" />}
                        </span>
                        <span className="min-w-0 flex-1">{entry.label}</span>
                      </button>
                    );
                  case 'radio':
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={entry.checked}
                        tabIndex={-1}
                        className="menu-item"
                        onClick={() => activate(entry)}
                      >
                        {entry.icon}
                        <span className="min-w-0 flex-1">{entry.label}</span>
                        {entry.checked && <Check aria-hidden className="size-3.5" />}
                      </button>
                    );
                }
              })}
            </div>
            {footer}
          </div>,
        )}
    </div>
  );
}
